import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { PromptMentionDropdown } from '@/components/agents/PromptMentionDropdown'
import { getDocumentMentionColor } from '@/components/agents/promptMentionsShared'
import type { MentionSuggestion } from '@/components/agents/promptMentionsShared'
import { cn } from '@/lib/utils'

type MentionMode = 'at' | 'variable'

export type PromptRichTool = {
  id: string
  name: string
  displayName: string
}

export type PromptRichTag = {
  id: string
  name: string
  color?: string
  description?: string
}

export type PromptRichDocument = {
  id: string
  title: string
  documentType?: string
  color?: string
}

type VariableOption = {
  token: string
  label: string
  description?: string
}

type PromptRichEditorProps = {
  value: string
  onChange: (value: string) => void
  tools: PromptRichTool[]
  tags: PromptRichTag[]
  documents: PromptRichDocument[]
  variableOptions: VariableOption[]
  disabled?: boolean
  placeholder?: string
  className?: string
  id?: string
  ariaLabel?: string
}

// Las pildoras del editor se pintan por DOM -son nodos que se crean a mano
// dentro de un contenteditable-, asi que no pueden llevar clases de Tailwind.
// Pero SI pueden llevar el token: `hsl(var(--x))` funciona igual en un estilo
// en linea. Antes eran hex fijos de modo oscuro, o sea que en modo claro se
// veian mal y nadie podia cambiarlos desde el sistema.
const TOOL_BADGE_BG = 'hsl(var(--muted))'
const TOOL_BADGE_BORDER = 'hsl(var(--border))'
const TOOL_BADGE_TEXT = 'hsl(var(--muted-foreground))'
const UNRESOLVED_BADGE_BG = 'hsl(var(--warning) / 0.1)'
const UNRESOLVED_BADGE_BORDER = 'hsl(var(--warning) / 0.3)'
const UNRESOLVED_BADGE_TEXT = 'hsl(var(--warning))'

const TAG_TOKEN_REGEX = /@tag:([a-zA-Z0-9_-]+)/g
const DOC_TOKEN_REGEX = /@doc:([0-9]+)/g
const MENTION_TOKEN_REGEX = /@([a-zA-Z][a-zA-Z0-9_]*)/g
const BLOCK_ELEMENT_TAGS = new Set(['DIV', 'P', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE'])

function isHexColor(value?: string): value is string {
  if (!value) return false
  return /^#[0-9a-fA-F]{6}$/.test(value)
}

function withAlpha(hex: string, alphaHex: string): string {
  return `${hex}${alphaHex}`
}

function parseHexChannel(hex: string, start: number): number {
  return Number.parseInt(hex.slice(start, start + 2), 16)
}

/** Blanco o negro sobre el color que el usuario eligio para su etiqueta. No es
 *  una eleccion de estilo: es lo que hace que el texto se lea encima. */
function contrastTextColor(hex?: string): string {
  // eslint-disable-next-line no-restricted-syntax -- contraste calculado sobre un color elegido por el usuario
  if (!isHexColor(hex)) return '#f8fafc'
  const r = parseHexChannel(hex, 1)
  const g = parseHexChannel(hex, 3)
  const b = parseHexChannel(hex, 5)
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  // eslint-disable-next-line no-restricted-syntax -- contraste calculado sobre un color elegido por el usuario
  return luminance > 0.55 ? '#0f172a' : '#f8fafc'
}

function hasBoundary(text: string, start: number): boolean {
  if (start <= 0) return true
  const previous = text[start - 1]
  return /\s/.test(previous) || `()[]{}<>'"\`.,;:!?`.includes(previous)
}

function createBadgeSpan(
  token: string,
  label: string,
  kind: 'tool' | 'tag' | 'document' | 'unresolved',
  color?: string
): HTMLSpanElement {
  const span = document.createElement('span')
  span.dataset.token = token
  span.dataset.kind = kind
  span.contentEditable = 'false'
  span.className =
    'mr-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium align-middle'
  if (kind === 'tool') {
    span.style.backgroundColor = TOOL_BADGE_BG
    span.style.borderColor = TOOL_BADGE_BORDER
    span.style.color = TOOL_BADGE_TEXT
  } else if ((kind === 'tag' || kind === 'document') && isHexColor(color)) {
    span.style.backgroundColor = withAlpha(color, '26')
    span.style.borderColor = withAlpha(color, '80')
    span.style.color = contrastTextColor(color)
  } else if (kind === 'tag') {
    span.style.backgroundColor = 'hsl(var(--success) / 0.1)'
    span.style.borderColor = 'hsl(var(--success) / 0.3)'
    span.style.color = 'hsl(var(--success))'
  } else if (kind === 'document') {
    span.style.backgroundColor = 'hsl(var(--primary) / 0.1)'
    span.style.borderColor = 'hsl(var(--primary) / 0.3)'
    span.style.color = 'hsl(var(--primary))'
  } else {
    span.style.backgroundColor = UNRESOLVED_BADGE_BG
    span.style.borderColor = UNRESOLVED_BADGE_BORDER
    span.style.color = UNRESOLVED_BADGE_TEXT
  }
  span.textContent = label
  return span
}

function appendPlain(root: HTMLElement, text: string): void {
  if (!text) return
  const parts = text.split('\n')
  parts.forEach((part, index) => {
    if (part) root.appendChild(document.createTextNode(part))
    if (index < parts.length - 1) root.appendChild(document.createElement('br'))
  })
}

function isBlockElement(node: Node): node is HTMLElement {
  return node.nodeType === Node.ELEMENT_NODE && BLOCK_ELEMENT_TAGS.has((node as HTMLElement).tagName)
}

function serializeEditorNode(node: Node): string {
  const element = node as HTMLElement
  if (element?.dataset?.token) {
    return element.dataset.token || ''
  }
  if (node.nodeType === Node.TEXT_NODE) {
    return node.nodeValue || ''
  }
  if ((node as HTMLElement).tagName === 'BR') {
    return '\n'
  }
  return serializeEditorChildren(node)
}

function serializeEditorChildren(root: Node): string {
  let out = ''
  const children = Array.from(root.childNodes)
  children.forEach((child, index) => {
    const part = serializeEditorNode(child)
    if (!part) return

    if (isBlockElement(child)) {
      if (out && !out.endsWith('\n')) {
        out += '\n'
      }
      out += part
      const hasFollowingContent = children
        .slice(index + 1)
        .some((sibling) => serializeEditorNode(sibling).length > 0)
      if (hasFollowingContent && !out.endsWith('\n')) {
        out += '\n'
      }
      return
    }

    out += part
  })
  return out
}

function serializeFromEditor(root: Node): string {
  return serializeEditorChildren(root)
}

type SerializedSelection = {
  start: number
  end: number
}

type SelectionBoundary = {
  container: Node
  offset: number
}

function rootContainsBoundary(root: HTMLElement, node: Node): boolean {
  return node === root || root.contains(node)
}

function readSerializedOffset(root: HTMLElement, container: Node, offset: number): number {
  const range = document.createRange()
  range.selectNodeContents(root)
  range.setEnd(container, offset)
  const fragment = range.cloneContents()
  const wrapper = document.createElement('div')
  wrapper.appendChild(fragment)
  return serializeFromEditor(wrapper).length
}

function readSerializedSelection(root: HTMLElement): SerializedSelection | null {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  if (!rootContainsBoundary(root, range.startContainer) || !rootContainsBoundary(root, range.endContainer)) {
    return null
  }
  return {
    start: readSerializedOffset(root, range.startContainer, range.startOffset),
    end: readSerializedOffset(root, range.endContainer, range.endOffset),
  }
}

function resolveSerializedBoundary(root: HTMLElement, targetOffset: number): SelectionBoundary {
  let remaining = Math.max(0, targetOffset)
  let boundary: SelectionBoundary | null = null

  const walk = (node: Node, parent: Node, index: number): void => {
    if (boundary) return
    const element = node as HTMLElement

    if (element?.dataset?.token) {
      const tokenLength = (element.dataset.token || '').length
      if (remaining === 0) {
        boundary = { container: parent, offset: index }
        return
      }
      if (remaining <= tokenLength) {
        boundary = { container: parent, offset: index + 1 }
        return
      }
      remaining -= tokenLength
      return
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const textLength = node.nodeValue?.length || 0
      if (remaining <= textLength) {
        boundary = { container: node, offset: remaining }
        return
      }
      remaining -= textLength
      return
    }

    if (element.tagName === 'BR') {
      if (remaining === 0) {
        boundary = { container: parent, offset: index }
        return
      }
      remaining -= 1
      if (remaining === 0) {
        boundary = { container: parent, offset: index + 1 }
      }
      return
    }

    Array.from(node.childNodes).forEach((child, childIndex) => {
      walk(child, node, childIndex)
    })
  }

  Array.from(root.childNodes).forEach((child, index) => {
    walk(child, root, index)
  })

  return boundary || { container: root, offset: root.childNodes.length }
}

function restoreSerializedSelection(root: HTMLElement, selectionOffsets: SerializedSelection): Range | null {
  const selection = window.getSelection()
  if (!selection) return null

  const startOffset = Math.max(0, selectionOffsets.start)
  const endOffset = Math.max(startOffset, selectionOffsets.end)
  const startBoundary = resolveSerializedBoundary(root, startOffset)
  const endBoundary = resolveSerializedBoundary(root, endOffset)
  const range = document.createRange()
  range.setStart(startBoundary.container, startBoundary.offset)
  range.setEnd(endBoundary.container, endBoundary.offset)
  selection.removeAllRanges()
  selection.addRange(range)
  return range
}

function renderEditorFromValue(
  root: HTMLElement,
  value: string,
  toolByName: Record<string, PromptRichTool>,
  tagById: Record<string, PromptRichTag>,
  docById: Record<string, PromptRichDocument>
): void {
  root.innerHTML = ''
  const text = value || ''
  const matches: Array<{
    start: number
    end: number
    token: string
    kind: 'tool' | 'tag' | 'document' | 'unresolved'
    label: string
    color?: string
  }> = []

  for (const match of text.matchAll(TAG_TOKEN_REGEX)) {
    const index = match.index ?? -1
    if (index < 0 || !hasBoundary(text, index)) continue
    const tagId = match[1]
    const token = `@tag:${tagId}`
    const tag = tagById[tagId]
    matches.push({
      start: index,
      end: index + token.length,
      token,
      kind: tag ? 'tag' : 'unresolved',
      label: tag ? `🏷️ ${tag.name}` : `⚠️ ${token}`,
      color: tag?.color,
    })
  }

  for (const match of text.matchAll(DOC_TOKEN_REGEX)) {
    const index = match.index ?? -1
    if (index < 0 || !hasBoundary(text, index)) continue
    const docId = match[1]
    const token = `@doc:${docId}`
    const document = docById[docId]
    const fallbackColor = getDocumentMentionColor(document?.documentType)
    matches.push({
      start: index,
      end: index + token.length,
      token,
      kind: document ? 'document' : 'unresolved',
      label: document ? `📃 ${document.title}` : `⚠️ ${token}`,
      color: document?.color || fallbackColor,
    })
  }

  const overlapsExisting = (start: number, end: number) =>
    matches.some((item) => start < item.end && end > item.start)

  for (const match of text.matchAll(MENTION_TOKEN_REGEX)) {
    const index = match.index ?? -1
    if (index < 0 || !hasBoundary(text, index)) continue
    const token = `@${match[1]}`
    const end = index + token.length
    if (overlapsExisting(index, end)) continue
    const lower = match[1].toLowerCase()
    const tool = toolByName[lower]
    if (token === '@tags' || token === '@tags_json') continue
    matches.push({
      start: index,
      end,
      token,
      kind: tool ? 'tool' : 'unresolved',
      label: tool ? `🔧 ${tool.name}` : `⚠️ ${token}`,
    })
  }

  matches.sort((a, b) => a.start - b.start)

  let cursor = 0
  for (const match of matches) {
    if (match.start < cursor) continue
    appendPlain(root, text.slice(cursor, match.start))
    root.appendChild(createBadgeSpan(match.token, match.label, match.kind, match.color))
    cursor = match.end
  }
  appendPlain(root, text.slice(cursor))
}

function openCaretRect(): DOMRect | null {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  const rects =
    typeof range.getClientRects === 'function'
      ? range.getClientRects()
      : ({ length: 0 } as DOMRectList)
  if (rects.length > 0) return rects[rects.length - 1]
  if (typeof range.getBoundingClientRect === 'function') {
    return range.getBoundingClientRect()
  }
  return null
}

function openCaretRectWithFallback(editor: HTMLElement | null): DOMRect | null {
  const rect = openCaretRect()
  if (rect && rect.left + rect.top + rect.width + rect.height > 0) return rect
  if (!editor) return null
  const selection = window.getSelection()
  if (!selection) return null
  const span = document.createElement('span')
  span.textContent = '\u200b'
  const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange()
  range.insertNode(span)
  const markerRect = span.getBoundingClientRect()
  const out = new DOMRect(markerRect.left, markerRect.top, markerRect.width, markerRect.height)
  span.parentNode?.removeChild(span)
  return out
}

function placeCaretAfter(node: Node): void {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.setStartAfter(node)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}

function placeCaretAtEnd(root: HTMLElement): void {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.selectNodeContents(root)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

function insertTextAtSelection(text: string): Range | null {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  range.deleteContents()
  const textNode = document.createTextNode(text)
  range.insertNode(textNode)

  const caretRange = document.createRange()
  caretRange.setStart(textNode, textNode.data.length)
  caretRange.collapse(true)
  selection.removeAllRanges()
  selection.addRange(caretRange)

  const startRange = document.createRange()
  startRange.setStart(textNode, 0)
  startRange.collapse(true)
  return startRange
}

function insertPlainTextAtSelection(text: string): void {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return
  const range = selection.getRangeAt(0)
  range.deleteContents()

  const normalizedText = text.replace(/\r\n?/g, '\n').replace(/\u00a0/g, ' ')
  const lines = normalizedText.split('\n')
  let insertedAnyNode = false

  lines.forEach((line, index) => {
    if (line.length > 0) {
      const textNode = document.createTextNode(line)
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      insertedAnyNode = true
    }
    if (index < lines.length - 1) {
      const brNode = document.createElement('br')
      range.insertNode(brNode)
      range.setStartAfter(brNode)
      range.collapse(true)
      insertedAnyNode = true
    }
  })

  if (!insertedAnyNode) {
    return
  }

  selection.removeAllRanges()
  selection.addRange(range)
}

function getVariableTriggerStartRange(selection: Selection): Range | null {
  if (selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  const container = range.startContainer

  if (container.nodeType === Node.TEXT_NODE) {
    const textNode = container as Text
    const offset = range.startOffset
    const previousChar = offset > 0 ? textNode.data[offset - 1] : ''
    if (previousChar !== '{') return null
    const startRange = document.createRange()
    startRange.setStart(textNode, Math.max(0, offset - 1))
    startRange.collapse(true)
    return startRange
  }

  if (container.nodeType === Node.ELEMENT_NODE) {
    const elementNode = container as Element
    const offset = range.startOffset
    if (offset <= 0) return null
    const previousNode = elementNode.childNodes[offset - 1]
    if (!previousNode || previousNode.nodeType !== Node.TEXT_NODE) return null

    const previousTextNode = previousNode as Text
    if (!previousTextNode.data.endsWith('{')) return null
    const startRange = document.createRange()
    startRange.setStart(previousTextNode, previousTextNode.data.length - 1)
    startRange.collapse(true)
    return startRange
  }

  return null
}

export function PromptRichEditor({
  value,
  onChange,
  tools,
  tags,
  documents,
  variableOptions,
  disabled = false,
  placeholder,
  className,
  id,
  ariaLabel,
}: PromptRichEditorProps) {
  const { t } = useTranslation()
  const editorRef = React.useRef<HTMLDivElement | null>(null)
  const wrapperRef = React.useRef<HTMLDivElement | null>(null)
  const lastSerializedValueRef = React.useRef<string>('')
  const lastRenderCatalogSignatureRef = React.useRef<string>('')
  const searchInputRef = React.useRef<HTMLInputElement | null>(null)
  const mentionSessionRef = React.useRef<{ mode: MentionMode; startRange: Range } | null>(null)
  const pendingTriggerRef = React.useRef<{ mode: MentionMode; startRange: Range } | null>(null)
  const lastCaretRangeRef = React.useRef<Range | null>(null)
  const [isEmpty, setIsEmpty] = React.useState(!(value || '').trim())
  const [menuMode, setMenuMode] = React.useState<MentionMode | null>(null)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [menuAnchor, setMenuAnchor] = React.useState<{ left: number; top: number } | null>(null)
  const [menuQuery, setMenuQuery] = React.useState('')
  const [selectedIndex, setSelectedIndex] = React.useState(0)

  const computeMenuAnchor = React.useCallback(() => {
    const rect = openCaretRectWithFallback(editorRef.current)
    const wrapper = wrapperRef.current
    if (!wrapper) {
      if (!rect) return { left: 8, top: 8 }
      return { left: rect.left, top: rect.bottom + 6 }
    }

    const wrapperRect = wrapper.getBoundingClientRect()
    const containerWidth = wrapperRect.width || wrapper.clientWidth
    const containerHeight = wrapperRect.height || wrapper.clientHeight
    if (!rect) {
      return { left: 8, top: 8, containerWidth, containerHeight }
    }
    return {
      left: rect.left - wrapperRect.left,
      top: rect.bottom - wrapperRect.top + 6,
      containerWidth,
      containerHeight,
    }
  }, [])

  const toolByName = React.useMemo(() => {
    const map: Record<string, PromptRichTool> = {}
    tools.forEach((tool) => {
      map[tool.name.toLowerCase()] = tool
    })
    return map
  }, [tools])

  const tagById = React.useMemo(() => {
    const map: Record<string, PromptRichTag> = {}
    tags.forEach((tag) => {
      map[tag.id] = tag
    })
    return map
  }, [tags])

  const docById = React.useMemo(() => {
    const map: Record<string, PromptRichDocument> = {}
    documents.forEach((document) => {
      map[String(document.id)] = document
    })
    return map
  }, [documents])

  const atSuggestions = React.useMemo<MentionSuggestion[]>(() => {
    const items: MentionSuggestion[] = []
    tools.forEach((tool) => {
      items.push({
        token: `@${tool.name}`,
        label: `🔧 ${tool.name}`,
        category: 'tool',
        description: tool.displayName,
        badgeEmoji: '🔧',
      })
    })
    tags.forEach((tag) => {
      items.push({
        token: `@tag:${tag.id}`,
        label: `🏷️ ${tag.name}`,
        category: 'tag',
        description: tag.description || tag.id,
        badgeEmoji: '🏷️',
        badgeColor: tag.color,
      })
    })
    documents.forEach((document) => {
      const color = document.color || getDocumentMentionColor(document.documentType)
      items.push({
        token: `@doc:${document.id}`,
        label: `📃 ${document.title}`,
        category: 'document',
        description: String(document.documentType || '').toUpperCase() || 'DOC',
        badgeEmoji: '📃',
        badgeColor: color,
      })
    })
    items.push({
      token: '@tags_json',
      label: t('promptMentions.allTags', { defaultValue: 'all tags' }),
      category: 'tag',
      description: t('promptMentions.allTagsJsonDescription', { defaultValue: 'JSON tags' }),
    })
    return items
  }, [documents, t, tags, tools])

  const variableSuggestions = React.useMemo<MentionSuggestion[]>(
    () =>
      variableOptions.map((item) => ({
        token: item.token,
        label: item.label,
        category: 'variable',
        description: item.description,
      })),
    [variableOptions]
  )

  const visibleSuggestions = React.useMemo(() => {
    const source = menuMode === 'variable' ? variableSuggestions : atSuggestions
    const query = menuQuery.trim().toLowerCase()
    if (!query) return source
    return source.filter((item) => {
      return (
        item.token.toLowerCase().includes(query) ||
        item.label.toLowerCase().includes(query) ||
        (item.description || '').toLowerCase().includes(query)
      )
    })
  }, [atSuggestions, menuMode, menuQuery, variableSuggestions])

  React.useEffect(() => {
    setSelectedIndex(0)
  }, [menuMode, menuQuery, menuOpen])

  const closeMenu = React.useCallback(() => {
    setMenuOpen(false)
    setMenuMode(null)
    setMenuAnchor(null)
    setMenuQuery('')
    mentionSessionRef.current = null
    pendingTriggerRef.current = null
  }, [])

  const readCurrentEditorRange = React.useCallback((): Range | null => {
    const root = editorRef.current
    if (!root) return null
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return null
    const range = selection.getRangeAt(0)
    if (!root.contains(range.commonAncestorContainer)) return null
    return range.cloneRange()
  }, [])

  const syncMenuFromSelection = React.useCallback(() => {
    const root = editorRef.current
    if (!root) return
    const caretRange = readCurrentEditorRange()
    if (caretRange) {
      lastCaretRangeRef.current = caretRange.cloneRange()
    }

    const pending = pendingTriggerRef.current
    if (pending && caretRange) {
      mentionSessionRef.current = {
        mode: pending.mode,
        startRange: pending.startRange.cloneRange(),
      }
      setMenuMode(pending.mode)
      setMenuOpen(true)
    }

    const session = mentionSessionRef.current
    if (!session || !caretRange) {
      if (menuOpen) closeMenu()
      return
    }

    let rawMention = ''
    try {
      const currentCaret = caretRange.cloneRange()
      if (
        currentCaret.compareBoundaryPoints(Range.START_TO_START, session.startRange) < 0 ||
        currentCaret.compareBoundaryPoints(Range.START_TO_END, session.startRange) < 0
      ) {
        closeMenu()
        return
      }

      const mentionRange = document.createRange()
      mentionRange.setStart(session.startRange.startContainer, session.startRange.startOffset)
      mentionRange.setEnd(currentCaret.startContainer, currentCaret.startOffset)
      rawMention = mentionRange.toString()
    } catch {
      closeMenu()
      return
    }

    let nextQuery = ''
    if (session.mode === 'at') {
      if (pending?.mode === 'at' && rawMention.length === 0) {
        setMenuMode('at')
        setMenuOpen(true)
        setMenuQuery('')
        setMenuAnchor(computeMenuAnchor())
        return
      }
      if (!rawMention.startsWith('@')) {
        closeMenu()
        return
      }
      pendingTriggerRef.current = null
      nextQuery = rawMention.slice(1)
      if (!/^[a-zA-Z0-9_.:-]*$/.test(nextQuery)) {
        closeMenu()
        return
      }
    } else {
      if (pending?.mode === 'variable' && (rawMention.length === 0 || rawMention === '{')) {
        setMenuMode('variable')
        setMenuOpen(true)
        setMenuQuery('')
        setMenuAnchor(computeMenuAnchor())
        return
      }
      if (!rawMention.startsWith('{{')) {
        closeMenu()
        return
      }
      pendingTriggerRef.current = null
      if (rawMention.includes('}}')) {
        closeMenu()
        return
      }
      nextQuery = rawMention.slice(2)
      if (!/^[a-zA-Z0-9_.]*$/.test(nextQuery)) {
        closeMenu()
        return
      }
    }

    setMenuMode(session.mode)
    setMenuOpen(true)
    setMenuQuery(nextQuery)
    setMenuAnchor(computeMenuAnchor())
  }, [closeMenu, computeMenuAnchor, menuOpen, readCurrentEditorRange])

  React.useEffect(() => {
    const root = editorRef.current
    if (!root) return
    const catalogSignature = [
      Object.keys(toolByName).sort().join(','),
      Object.keys(tagById).sort().join(','),
      Object.keys(docById).sort().join(','),
    ].join('|')
    const sameValue = value === lastSerializedValueRef.current
    const sameCatalog = catalogSignature === lastRenderCatalogSignatureRef.current
    if (sameValue && sameCatalog) return
    const activeSelection = document.activeElement === root ? readSerializedSelection(root) : null
    renderEditorFromValue(root, value, toolByName, tagById, docById)
    lastSerializedValueRef.current = value
    lastRenderCatalogSignatureRef.current = catalogSignature
    setIsEmpty((root.innerText || '').trim().length === 0)
    if (activeSelection) {
      root.focus({ preventScroll: true })
      const restoredRange = restoreSerializedSelection(root, activeSelection)
      if (restoredRange) {
        lastCaretRangeRef.current = restoredRange.cloneRange()
      }
    } else if (document.activeElement !== root) {
      placeCaretAtEnd(root)
    }
  }, [docById, tagById, toolByName, value])

  React.useEffect(() => {
    if (!menuOpen) return
    const handler = () => {
      setMenuAnchor(computeMenuAnchor())
    }
    window.addEventListener('scroll', handler, true)
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('scroll', handler, true)
      window.removeEventListener('resize', handler)
    }
  }, [computeMenuAnchor, menuOpen])

  React.useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (
        target instanceof Element &&
        target.closest('[data-prompt-mention-dropdown="true"]')
      ) {
        return
      }
      closeMenu()
    }
    window.addEventListener('mousedown', onPointerDown)
    return () => window.removeEventListener('mousedown', onPointerDown)
  }, [closeMenu, menuOpen])

  React.useEffect(() => {
    if (!menuOpen) return
    const onSelectionChange = () => {
      syncMenuFromSelection()
    }
    document.addEventListener('selectionchange', onSelectionChange)
    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, [menuOpen, syncMenuFromSelection])

  const emitChange = React.useCallback(() => {
    const root = editorRef.current
    if (!root) return
    const next = serializeFromEditor(root)
    lastSerializedValueRef.current = next
    setIsEmpty((root.innerText || '').trim().length === 0)
    onChange(next)
  }, [onChange])

  const insertSuggestion = React.useCallback(
    (suggestion: MentionSuggestion) => {
      const root = editorRef.current
      if (!root || disabled) return
      const session = mentionSessionRef.current
      const caretRange = readCurrentEditorRange() || lastCaretRangeRef.current
      if (!session || !caretRange) {
        closeMenu()
        return
      }

      const replaceRange = document.createRange()
      replaceRange.setStart(session.startRange.startContainer, session.startRange.startOffset)
      replaceRange.setEnd(caretRange.startContainer, caretRange.startOffset)
      if (!root.contains(replaceRange.commonAncestorContainer)) {
        closeMenu()
        return
      }

      if (document.activeElement !== root) {
        root.focus({ preventScroll: true })
      }
      const selection = window.getSelection()
      if (!selection) {
        closeMenu()
        return
      }
      selection.removeAllRanges()
      selection.addRange(replaceRange)

      replaceRange.deleteContents()
      let insertedNode: Node
      if (menuMode === 'variable') {
        insertedNode = document.createTextNode(`{{${suggestion.token}}}`)
      } else {
        if (suggestion.token === '@tags_json') {
          insertedNode = document.createTextNode(suggestion.token)
        } else if (suggestion.category === 'tag') {
          insertedNode = createBadgeSpan(
            suggestion.token,
            suggestion.label,
            'tag',
            suggestion.badgeColor
          )
        } else if (suggestion.category === 'document') {
          insertedNode = createBadgeSpan(
            suggestion.token,
            suggestion.label,
            'document',
            suggestion.badgeColor
          )
        } else {
          insertedNode = createBadgeSpan(
            suggestion.token,
            suggestion.label,
            'tool'
          )
        }
      }

      replaceRange.insertNode(insertedNode)
      const trailingSpace = document.createTextNode(' ')
      if (insertedNode.parentNode) {
        insertedNode.parentNode.insertBefore(trailingSpace, insertedNode.nextSibling)
      }
      placeCaretAfter(trailingSpace)

      closeMenu()
      emitChange()
    },
    [closeMenu, disabled, emitChange, menuMode, readCurrentEditorRange]
  )

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return

      if (menuOpen && visibleSuggestions.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          setSelectedIndex((prev) => (prev + 1) % visibleSuggestions.length)
          return
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          setSelectedIndex((prev) => (prev - 1 + visibleSuggestions.length) % visibleSuggestions.length)
          return
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault()
          insertSuggestion(visibleSuggestions[selectedIndex] || visibleSuggestions[0])
          return
        }
      }

      if (event.key === 'Escape' && menuOpen) {
        event.preventDefault()
        closeMenu()
        return
      }

      if (event.key === '@') {
        event.preventDefault()
        const insertedStart = insertTextAtSelection('@')
        if (insertedStart) {
          mentionSessionRef.current = {
            mode: 'at',
            startRange: insertedStart.cloneRange(),
          }
          pendingTriggerRef.current = {
            mode: 'at',
            startRange: insertedStart.cloneRange(),
          }
          lastCaretRangeRef.current = readCurrentEditorRange()
          setMenuMode('at')
          setMenuOpen(true)
          setMenuQuery('')
          setMenuAnchor(computeMenuAnchor())
          emitChange()
        }
        return
      }

      if (event.key === '{') {
        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) return
        const variableStartRange = getVariableTriggerStartRange(selection)
        if (variableStartRange) {
          mentionSessionRef.current = {
            mode: 'variable',
            startRange: variableStartRange.cloneRange(),
          }
          pendingTriggerRef.current = {
            mode: 'variable',
            startRange: variableStartRange,
          }
          setMenuMode('variable')
          setMenuOpen(true)
          setMenuQuery('')
          setMenuAnchor(computeMenuAnchor())
        }
      }
    },
    [closeMenu, computeMenuAnchor, disabled, emitChange, insertSuggestion, menuOpen, readCurrentEditorRange, selectedIndex, visibleSuggestions]
  )

  const handleInput = React.useCallback(() => {
    emitChange()
    syncMenuFromSelection()
  }, [emitChange, syncMenuFromSelection])

  const handleEditorClick = React.useCallback(() => {
    syncMenuFromSelection()
  }, [syncMenuFromSelection])

  const handleEditorMouseUp = React.useCallback(() => {
    syncMenuFromSelection()
  }, [syncMenuFromSelection])

  const handleEditorKeyUp = React.useCallback(() => {
    syncMenuFromSelection()
  }, [syncMenuFromSelection])

  const handlePaste = React.useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return
    event.preventDefault()
    const htmlText = event.clipboardData.getData('text/html')
    let plainText =
      event.clipboardData.getData('text/plain') ||
      event.clipboardData.getData('text') ||
      ''
    if (!plainText && htmlText) {
      const parser = document.createElement('div')
      parser.innerHTML = htmlText
      plainText = parser.innerText || parser.textContent || ''
    }
    if (!plainText) return
    insertPlainTextAtSelection(plainText)
    emitChange()
    syncMenuFromSelection()
  }, [disabled, emitChange, syncMenuFromSelection])

  React.useEffect(() => {
    if (!menuOpen) return
    syncMenuFromSelection()
  }, [menuOpen, syncMenuFromSelection])

  React.useEffect(() => {
    if (!menuOpen) return
    if (visibleSuggestions.length > 0) return
    if (!mentionSessionRef.current) {
      closeMenu()
    }
  }, [closeMenu, menuOpen, visibleSuggestions.length])

  React.useEffect(() => {
    return () => {
      mentionSessionRef.current = null
      pendingTriggerRef.current = null
      lastCaretRangeRef.current = null
    }
  }, [])

  React.useEffect(() => {
    const root = editorRef.current
    if (!root) return
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const currentRange = selection.getRangeAt(0)
    if (!root.contains(currentRange.commonAncestorContainer)) return
    lastCaretRangeRef.current = currentRange.cloneRange()
  }, [value])

  React.useEffect(() => {
    if (!menuOpen) return
    setMenuAnchor(computeMenuAnchor())
  }, [computeMenuAnchor, menuOpen, menuQuery, menuMode])

  React.useEffect(() => {
    if (!menuOpen) return
    const root = editorRef.current
    if (!root) return
    const onBlur = () => {
      window.setTimeout(() => {
        const active = document.activeElement
        if (
          active instanceof Element &&
          active.closest('[data-prompt-mention-dropdown="true"]')
        ) {
          return
        }
        if (active === root) {
          return
        }
        closeMenu()
      }, 0)
    }
    root.addEventListener('blur', onBlur)
    return () => root.removeEventListener('blur', onBlur)
  }, [closeMenu, menuOpen])

  React.useEffect(() => {
    if (!menuOpen) return
    if (!mentionSessionRef.current) return
    setMenuAnchor(computeMenuAnchor())
  }, [computeMenuAnchor, menuOpen, value])

  React.useEffect(() => {
    if (!menuOpen) return
    const root = editorRef.current
    if (!root) return
    const onScroll = () => {
      setMenuAnchor(computeMenuAnchor())
    }
    root.addEventListener('scroll', onScroll)
    return () => root.removeEventListener('scroll', onScroll)
  }, [computeMenuAnchor, menuOpen])

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      {isEmpty && placeholder ? (
        <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
          {placeholder}
        </div>
      ) : null}
      <div
        ref={editorRef}
        id={id}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline
        contentEditable={!disabled}
        suppressContentEditableWarning
        className={cn(
          'min-h-[190px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm',
          'break-words [overflow-wrap:anywhere]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-60'
        )}
        onInput={handleInput}
        onClick={handleEditorClick}
        onMouseUp={handleEditorMouseUp}
        onKeyUp={handleEditorKeyUp}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />

      <PromptMentionDropdown
        open={menuOpen && visibleSuggestions.length > 0}
        anchor={menuAnchor}
        suggestions={visibleSuggestions}
        selectedIndex={selectedIndex}
        onSelect={insertSuggestion}
        helpText={t('agent_form.fields.prompt_mentions_help')}
        systemLabel={t('agent_form.fields.prompt_mentions_group_system')}
        toolLabel={t('agent_form.fields.prompt_mentions_group_tools')}
        tagLabel={t('agent_form.fields.prompt_mentions_group_tags')}
        documentLabel={t('agent_form.fields.prompt_mentions_group_documents')}
        variableLabel={t('agent_form.fields.prompt_mentions_group_variables')}
        disablePortal
        searchValue={menuQuery}
        onSearchChange={setMenuQuery}
        searchInputRef={searchInputRef}
        filterPlaceholder={t('agent_form.fields.prompt_mentions_filter_placeholder')}
      />
    </div>
  )
}
